// src/pages/FormularioCV.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import Hero from '../components/Hero';
import './FormularioCV.css';

const API = 'https://api.sevicol.com.uy';

const FormularioCV = () => {
  const [fotoPreview, setFotoPreview] = useState(null);
  const fotoRef = useRef(null);
  const revokePreview = (url) => { try { url && URL.revokeObjectURL(url); } catch (_) {} };

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    documento: '',        // 👈 NUEVO
    fecha_nac: '',
    foto: null,
    cv: null,
    direccion: '',
    departamento: '',
    ciudad: '',
    telefono: '',
    email: '',
    cargo: '',
    situacion_laboral: 'Sin trabajo',
    detalle_situacion: '',
    estudios: '',
    ref_laborales: '',
    ref_personales: '',
    motivacion: ''
  });

  const [errors, setErrors] = useState({});
  const [ubicaciones, setUbicaciones] = useState({});
  const [mensaje, setMensaje] = useState('');
  const [variant, setVariant] = useState('info');

  useEffect(() => {
    axios.get(`${API}/api/ubicaciones`)
      .then(res => setUbicaciones(res.data || {}))
      .catch(err => console.error('Error ubicaciones:', err));
  }, []);

  useEffect(() => () => revokePreview(fotoPreview), [fotoPreview]);

  const validators = {
    nombre: (v) => v?.trim() ? '' : 'Ingresá tu nombre.',
    apellido: (v) => v?.trim() ? '' : 'Ingresá tu apellido.',
    documento: (v) => {
      const limpio = (v || '').replace(/\s+/g, '');
      if (!limpio) return 'Ingresá tu documento de identidad.';
      if (!/^\d{8}$/.test(limpio)) return 'Ingresá el documento sin puntos ni guiones (8 dígitos).';
      return '';
    },
    fecha_nac: (v) => v ? '' : 'Seleccioná tu fecha de nacimiento.',
    direccion: (v) => v?.trim() ? '' : 'Ingresá tu dirección.',
    departamento: (v) => v ? '' : 'Seleccioná un departamento.',
    ciudad: (v) => v ? '' : 'Seleccioná una ciudad.',
    telefono: (v) => v?.trim() ? '' : 'Ingresá un teléfono.',
    email: (v) => /\S+@\S+\.\S+/.test(v) ? '' : 'Ingresá un email válido.',
    cargo: (v) => v ? '' : 'Seleccioná un puesto.',
    estudios: (v) => v?.trim() ? '' : 'Ingresá tus estudios.'  // 👈 obligatorio
  };

  const validateField = (name, value) => {
    if (!validators[name]) return '';
    const msg = validators[name](value);
    setErrors(prev => ({ ...prev, [name]: msg }));
    return msg;
  };

  const validateAll = () => {
    const next = {};
    Object.keys(validators).forEach((k) => {
      next[k] = validators[k](formData[k]);
    });
    setErrors(next);
    const firstInvalid = Object.entries(next).find(([, v]) => v);
    return firstInvalid?.[0] || null;
  };

  const scrollToField = (name) => {
    const el = document.querySelector(`[data-field="${name}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    if (type === 'file') {
      const f = files?.[0] || null;
      setFormData(prev => ({ ...prev, [name]: f }));
      if (name === 'foto') {
        if (f) {
          revokePreview(fotoPreview);
          setFotoPreview(URL.createObjectURL(f));
        } else {
          revokePreview(fotoPreview);
          setFotoPreview(null);
        }
      }
      setErrors(prev => ({ ...prev, [name]: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (validators[name]) validateField(name, value);
    }
  };

  const clearFoto = () => {
    setFormData(prev => ({ ...prev, foto: null }));
    revokePreview(fotoPreview);
    setFotoPreview(null);
    if (fotoRef.current) fotoRef.current.value = '';
  };

  const handleDepartamentoChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, departamento: value, ciudad: '' }));
    validateField('departamento', value);
    setErrors(prev => ({ ...prev, ciudad: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje('');

    const firstInvalid = validateAll();
    if (firstInvalid) {
      setVariant('danger');
      setMensaje('Revisá los campos marcados.');
      scrollToField(firstInvalid);
      return;
    }

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== null && v !== undefined) data.append(k, v);
      });

      await axios.post(`${API}/api/cv/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setVariant('success');
      setMensaje('¡Gracias! Recibimos tu postulación.');

      setFormData({
        nombre: '',
        apellido: '',
        documento: '',
        fecha_nac: '',
        foto: null,
        cv: null,
        direccion: '',
        departamento: '',
        ciudad: '',
        telefono: '',
        email: '',
        cargo: '',
        situacion_laboral: 'Sin trabajo',
        detalle_situacion: '',
        estudios: '',
        ref_laborales: '',
        ref_personales: '',
        motivacion: ''
      });
      setErrors({});
      clearFoto();
      setTimeout(() => setMensaje(''), 5000);
    } catch (err) {
      setVariant('danger');
      setMensaje(err?.response?.data?.error || 'Error al enviar la postulación');
      setTimeout(() => setMensaje(''), 5000);
    }
  };

  return (
    <>
      <Hero
        subtitle="RECURSOS HUMANOS"
        title="Envíanos tu currículum"
        description="Sumate a SEVICOL. Cargá tus datos y contanos por qué querés trabajar con nosotros."
        variant="dark"
        align="center"
        height="sm"
      />

      <main className="cv-page">
        <section className="cv-container card-like">
          <h3 className="cv-heading">Completa tus datos</h3>
          <p className="text-muted">Los campos señalados con (*) son obligatorios</p>

          <Form noValidate onSubmit={handleSubmit} className="cv-form">

            <Row className="g-3">
              <Col md={4}>
                <Form.Group data-field="nombre">
                  <Form.Label>Nombre *</Form.Label>
                  {errors.nombre && <div className="field-error">{errors.nombre}</div>}
                  <Form.Control
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    onBlur={(e) => validateField('nombre', e.target.value)}
                    isInvalid={!!errors.nombre}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group data-field="apellido">
                  <Form.Label>Apellido *</Form.Label>
                  {errors.apellido && <div className="field-error">{errors.apellido}</div>}
                  <Form.Control
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    onBlur={(e) => validateField('apellido', e.target.value)}
                    isInvalid={!!errors.apellido}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group data-field="documento">
                  <Form.Label>Documento de identidad *</Form.Label>                  
                  {errors.documento && <div className="field-error">{errors.documento}</div>}
                  <Form.Control
                    name="documento"
                    value={formData.documento}
                    onChange={handleChange}
                    onBlur={(e) => validateField('documento', e.target.value)}
                    isInvalid={!!errors.documento}
                    placeholder="Sin puntos ni guiones"
                    inputMode="numeric"
                    maxLength={8}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-0">
              <Col md={4}>
                <Form.Group data-field="fecha_nac">
                  <Form.Label>Fecha de nacimiento *</Form.Label>
                  {errors.fecha_nac && <div className="field-error">{errors.fecha_nac}</div>}
                  <Form.Control
                    type="date"
                    name="fecha_nac"
                    value={formData.fecha_nac}
                    onChange={handleChange}
                    onBlur={(e) => validateField('fecha_nac', e.target.value)}
                    isInvalid={!!errors.fecha_nac}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Foto (JPG/PNG/WEBP)</Form.Label>
                  <Form.Control
                    type="file"
                    name="foto"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleChange}
                    ref={fotoRef}
                  />
                  {fotoPreview && (
                    <div className="cv-photoPreview">
                      <img src={fotoPreview} alt="Previsualización" />
                      <button type="button" className="cv-photoRemove" onClick={clearFoto}>
                        Quitar
                      </button>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-0">
              <Col md={6}>
                <Form.Group data-field="direccion">
                  <Form.Label>Dirección *</Form.Label>
                  {errors.direccion && <div className="field-error">{errors.direccion}</div>}
                  <Form.Control
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    onBlur={(e) => validateField('direccion', e.target.value)}
                    isInvalid={!!errors.direccion}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group data-field="departamento">
                  <Form.Label>Departamento *</Form.Label>
                  {errors.departamento && <div className="field-error">{errors.departamento}</div>}
                  <Form.Select
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleDepartamentoChange}
                    onBlur={(e) => validateField('departamento', e.target.value)}
                    isInvalid={!!errors.departamento}
                    required
                  >
                    <option value="">Selecciona un departamento</option>
                    {Object.keys(ubicaciones).map(dep => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group data-field="ciudad">
                  <Form.Label>Ciudad *</Form.Label>
                  {errors.ciudad && <div className="field-error">{errors.ciudad}</div>}
                  <Form.Select
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                    onBlur={(e) => validateField('ciudad', e.target.value)}
                    isInvalid={!!errors.ciudad}
                    required
                    disabled={!formData.departamento}
                  >
                    <option value="">Selecciona una ciudad</option>
                    {formData.departamento &&
                      (ubicaciones[formData.departamento] || []).map(ci => (
                        <option key={ci} value={ci}>{ci}</option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-0">
              <Col md={3}>
                <Form.Group data-field="telefono">
                  <Form.Label>Teléfono *</Form.Label>
                  {errors.telefono && <div className="field-error">{errors.telefono}</div>}
                  <Form.Control
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    onBlur={(e) => validateField('telefono', e.target.value)}
                    isInvalid={!!errors.telefono}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group data-field="email">
                  <Form.Label>Email *</Form.Label>
                  {errors.email && <div className="field-error">{errors.email}</div>}
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={(e) => validateField('email', e.target.value)}
                    isInvalid={!!errors.email}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group data-field="cargo">
                  <Form.Label>Puesto *</Form.Label>
                  {errors.cargo && <div className="field-error">{errors.cargo}</div>}
                  <Form.Select
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleChange}
                    onBlur={(e) => validateField('cargo', e.target.value)}
                    isInvalid={!!errors.cargo}
                    required
                  >
                    <option value="">Selecciona un puesto</option>
                    <option value="Administrativo">Administrativo</option>
                    <option value="Limpieza">Limpieza</option>
                    <option value="Técnico">Técnico</option>
                    <option value="Guardia Físico">Guardia Físico</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-0">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Situación laboral</Form.Label>
                  <Form.Select
                    name="situacion_laboral"
                    value={formData.situacion_laboral}
                    onChange={handleChange}
                  >
                    <option>Sin trabajo</option>
                    <option>Con trabajo</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Detalle de la situación laboral</Form.Label>
                  <Form.Control
                    name="detalle_situacion"
                    value={formData.detalle_situacion}
                    onChange={handleChange}
                    placeholder="Disponibilidad, horarios, aviso previo, etc."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-2">
              <Form.Label>Currículum (PDF/DOC/DOCX)</Form.Label>
              <Form.Control
                type="file"
                name="cv"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Tamaño máximo 25 MB. Formatos permitidos: PDF, DOC, DOCX.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mt-3" data-field="estudios">
              <Form.Label>Estudios *</Form.Label>
              {errors.estudios && <div className="field-error">{errors.estudios}</div>}
              <Form.Control
                as="textarea"
                rows={3}
                name="estudios"
                value={formData.estudios}
                onChange={handleChange}
                onBlur={(e) => validateField('estudios', e.target.value)}
                isInvalid={!!errors.estudios}
                required
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Referencias laborales</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="ref_laborales"
                value={formData.ref_laborales}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Referencias personales</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="ref_personales"
                value={formData.ref_personales}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>¿Por qué quiere trabajar en SEVICOL?</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="motivacion"
                value={formData.motivacion}
                onChange={handleChange}
              />
            </Form.Group>

            <div className="cv-actions">
              <Button type="submit" className="mt-3">Enviar</Button>
            </div>

            {mensaje && <Alert className="mt-3" variant={variant}>{mensaje}</Alert>}
          </Form>
        </section>
      </main>
    </>
  );
};

export default FormularioCV;






